<template lang="pug">
el-dialog.study-result-modal(
  :title="modalTitle"
  :visible.sync="visible"
  width="520px"
  :close-on-click-modal="false"
  @close="onClose"
)
  .srm-body
    //- Fecha de realización
    .srm-field
      label.srm-label * Fecha de realización
      el-date-picker(
        v-model="form.performedAt"
        type="date"
        placeholder="Seleccioná la fecha"
        format="dd/MM/yyyy"
        value-format="yyyy-MM-dd"
        style="width: 100%"
      )

    //- Descripción
    .srm-field
      label.srm-label Descripción
      el-input(
        v-model="form.description"
        placeholder="Ej: Hemograma completo — Lab. Hidalgo"
      )

    //- Links externos
    .srm-field
      label.srm-label Links al estudio
      .srm-links
        .srm-link-row(v-for="(link, idx) in form.externalLinks" :key="idx")
          el-input(
            v-model="link.label"
            placeholder="Etiqueta (ej: Ver estudio)"
            style="width: 140px; margin-right: 8px"
          )
          el-input(
            v-model="link.url"
            placeholder="https://..."
            style="flex: 1; margin-right: 8px"
          )
          el-button(
            type="text"
            icon="el-icon-delete"
            @click="removeLink(idx)"
          )
        el-button(
          type="text"
          icon="el-icon-plus"
          @click="addLink"
          style="margin-top: 4px"
        ) Agregar link

    //- Archivos adjuntos
    .srm-field
      label.srm-label Archivos adjuntos
      el-upload(
        ref="uploader"
        action=""
        :auto-upload="false"
        :file-list="fileList"
        :on-change="onFileChange"
        :on-remove="onFileRemove"
        multiple
        accept=".pdf,.jpg,.jpeg,.png"
      )
        el-button(icon="el-icon-paperclip") Adjuntar archivos
        div(slot="tip" style="font-size: 11px; color: #94a3b8; margin-top: 4px") PDF, JPG o PNG

  span(slot="footer")
    el-button(@click="onClose") Cancelar
    el-button(
      type="primary"
      :loading="saving"
      :disabled="!form.performedAt"
      @click="save"
    ) Guardar resultado
</template>

<script>
/**
 * @component StudyResultModal
 * @description Modal para cargar resultados de estudios de laboratorio o imágenes.
 * Crea una indication de tipo labResults/imagesResults y sube los archivos adjuntos.
 * Reutilizable desde PatientHistory, PatientAppointmentView y evolución standalone.
 */
import indicationService from "@/services/indication";
import api from "@/services/api";

export default {
  name: "StudyResultModal",

  props: {
    /** @prop {boolean} visible - Controla visibilidad del modal */
    value: { type: Boolean, default: false },
    /** @prop {string} patientId - ID del paciente */
    patientId: { type: String, required: true },
    /** @prop {string} type - 'lab' | 'images' — tipo de la orden origen */
    type: { type: String, default: "lab" },
    /** @prop {Object|null} sourceIndication - Orden origen (opcional) */
    sourceIndication: { type: Object, default: null },
    /** @prop {string|null} appointmentId - ID de la atención (opcional) */
    appointmentId: { type: String, default: null },
  },

  data() {
    return {
      /** @description Estado de guardado */
      saving: false,
      /** @description Lista de archivos para el uploader */
      fileList: [],
      /** @description Archivos raw para subir */
      rawFiles: [],
      /** @description Formulario del resultado */
      form: {
        performedAt: null,
        description: "",
        externalLinks: [],
      },
    };
  },

  computed: {
    /**
     * @description Visibilidad del modal via v-model
     * @returns {boolean}
     */
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    /**
     * @description Título del modal según el tipo de estudio
     * @returns {string}
     */
    modalTitle() {
      return this.type === "images"
        ? "Cargar resultado de imagen"
        : "Cargar resultado de laboratorio";
    },

    /**
     * @description Tipo de indication resultado según el tipo de orden
     * @returns {string}
     */
    resultType() {
      return this.type === "images" ? "imagesResults" : "labResults";
    },
  },

  methods: {
    /**
     * @description Agrega un link externo vacío al formulario
     */
    addLink() {
      this.form.externalLinks.push({ label: "", url: "" });
    },

    /**
     * @description Elimina un link externo por índice
     * @param {number} idx
     */
    removeLink(idx) {
      this.form.externalLinks.splice(idx, 1);
    },

    /**
     * @description Handler de cambio de archivo en el uploader
     * @param {Object} file - Archivo de Element UI
     * @param {Array} fileList - Lista actualizada
     */
    onFileChange(file, fileList) {
      this.fileList = fileList;
      this.rawFiles = fileList.map((f) => f.raw).filter(Boolean);
    },

    /**
     * @description Handler de eliminación de archivo
     * @param {Object} file
     * @param {Array} fileList
     */
    onFileRemove(file, fileList) {
      this.fileList = fileList;
      this.rawFiles = fileList.map((f) => f.raw).filter(Boolean);
    },

    /**
     * @description Guarda el resultado: crea la indication y sube los archivos.
     * Flujo: POST /indications → PUT /indications/:id/files (si hay archivos)
     */
    async save() {
      if (!this.form.performedAt) {
        this.$message.warning("La fecha de realización es obligatoria");
        return;
      }

      this.saving = true;
      try {
        // Filtrar links con URL válida
        const externalLinks = this.form.externalLinks.filter(
          (l) => l.url && l.url.trim()
        );

        // Crear la indication resultado
        const payload = {
          type: this.resultType,
          patientId: this.patientId,
          performedAt: this.form.performedAt,
          description: this.form.description || undefined,
          externalLinks: externalLinks.length ? externalLinks : undefined,
          source: "manual",
          ...(this.sourceIndication
            ? { resultOf: this.sourceIndication._id }
            : {}),
          ...(this.appointmentId ? { appointment: this.appointmentId } : {}),
        };

        const indication = await indicationService.createIndication(payload);

        // Subir archivos si hay
        if (this.rawFiles.length) {
          const formData = new FormData();
          this.rawFiles.forEach((file) => formData.append("files", file));
          await api.put(`/indications/${indication._id}/files`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        this.$message.success("Resultado guardado correctamente");
        this.$emit("saved", indication);
        this.onClose();
      } catch (err) {
        console.error("Error guardando resultado:", err);
        this.$message.error("Error al guardar el resultado");
      } finally {
        this.saving = false;
      }
    },

    /**
     * @description Cierra el modal y resetea el formulario
     */
    onClose() {
      this.visible = false;
      this.form = { performedAt: null, description: "", externalLinks: [] };
      this.fileList = [];
      this.rawFiles = [];
    },
  },
};
</script>

<style scoped>
.srm-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.srm-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.srm-label {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.srm-links {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.srm-link-row {
  display: flex;
  align-items: center;
}
</style>
